import React, { useMemo } from 'react';
import _isInteger from 'lodash/isInteger';

import { useTranslation } from '@lyearn/i18n';
import { Box, Scrollable, Stack, Typography } from '@lyearn/ui';
import RichTextViewer from '@/components/RichTextViewer';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import QuizSettingsEntity from '@/modules/corporate/modules/quiz/entity/QuizSettings';
import { getStatus } from '@/modules/corporate/modules/quiz/utils';
import { getQuizTimerDuration } from '@/modules/corporate/modules/quiz/views/QuizDetails/components/QuizDetailsHero/utils';
import QuizDetailsProvider from '@/modules/corporate/modules/quiz/views/QuizDetails/context/QuizDetailsContext';
import getQuizModifiedAttempts from '@/modules/corporate/modules/quiz/views/QuizDetails/utils/getQuizModifiedAttempts';

import Score from '../Score';
import QuizPreviewActions from './components/QuizPreviewActions';
import { QuizPreviewType } from './types';

const PopupQuizPreview = (props: QuizPreviewType) => {
  const { quiz, quizPerformance, popupQuizData } = props;
  const { t } = useTranslation(['QuizPlayer']);
  const user = useUserContext();
  const { showModal, showQuizPlayer, onOpen, onClose, quizPlayerProps, reviewAttemptId, ...rest } =
    popupQuizData;
  const userMaxScore = quizPerformance?.maxSubmissionDetails?.score ?? 0;
  const quizMaxScore = quizPerformance?.maxScore ?? quiz.maxScore;
  const timeLimitApplicable = quiz.settings?.timeLimitApplicableOn;

  const maxAttempts = QuizSettingsEntity.getAllowedAttempts(quiz.settings);
  const attemptsCount = useMemo(
    () => getQuizModifiedAttempts(quizPerformance).length,
    [quizPerformance],
  );

  const status = useMemo(
    () => getStatus({ quiz, performance: quizPerformance, t }),
    [quiz, quizPerformance, t],
  );

  return (
    <Box className="relative flex flex-col py-20 px-24 h-full">
      <Scrollable className="mb-20">
        <Typography className="mb-12" color="text-text-primary" variant="display-xl">
          {quiz.name}
        </Typography>
        {quiz.description && (
          <RichTextViewer
            className="mb-12"
            color="text-text-secondary"
            html={quiz.description}
            variant="body-long-01"
          />
        )}
        <Stack className="mt-12 grid grid-cols-2 py-8" gapX={48} gapY={24}>
          <Stack direction="col">
            <Typography className="mb-4" color="text-text-secondary" variant="label-02">
              {t('QuizPlayer:QuizLength')}
            </Typography>
            <Typography color="text-text-primary" variant="heading-m">
              {t('QuizPlayer:questions', { count: quiz.quizQuestionsConfig.questionCount })}
            </Typography>
          </Stack>
          <Stack direction="col">
            <Typography className="mb-4" color="text-text-secondary" variant="label-02">
              {t('QuizPlayer:MaxScore')}
            </Typography>
            <Typography color="text-text-primary" variant="heading-m">
              {t('QuizPlayer:points', { count: quizMaxScore })}
            </Typography>
          </Stack>
          {timeLimitApplicable && (
            <Stack direction="col">
              <Typography className="mb-4" color="text-text-secondary" variant="label-02">
                {t('QuizPlayer:TimeLimit')}
              </Typography>
              <Typography color="text-text-primary" variant="heading-m">
                {getQuizTimerDuration(t, quiz).slice(6)}
              </Typography>
            </Stack>
          )}
          {_isInteger(userMaxScore) && (
            <Stack direction="col">
              <Typography className="mb-4" color="text-text-secondary" variant="label-02">
                {t('QuizPlayer:Attempts')}
              </Typography>
              <Stack alignItems="center">
                <Typography color="text-text-primary" variant="heading-m">
                  {attemptsCount}/{maxAttempts}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
        {status ? (
          <Score
            overrides={{
              title: 'heading-m',
              description: 'body-short-01',
              descriptionLabel: 'label-01',
            }}
            score={userMaxScore}
            status={status.result}
          />
        ) : null}
      </Scrollable>
      <QuizDetailsProvider
        {...rest}
        languageType={quiz.languageType ?? undefined}
        userId={user.id}
        onBackToHome={onClose}>
        <QuizPreviewActions {...props} />
      </QuizDetailsProvider>
    </Box>
  );
};

export default PopupQuizPreview;
